import React from 'react';
import { Chip, Typography, alpha, SxProps, Theme } from '@mui/material';
import { tagColor } from '../../utils/tagColor';

interface Props {
  value?: string | null;
  /** Default 'small'. */
  size?: 'small' | 'medium';
  /** Placeholder when `value` is empty/null. Defaults to an em dash. */
  emptyPlaceholder?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Soft-fill chip that colours itself from the string value via `tagColor`.
 * Same value → same colour everywhere, regardless of page or row.
 *
 * Use for taxonomy badges (category, sub-category, label), NOT for
 * semantic UI states (status, severity) — those have their own dedicated
 * components so their colour is a system guarantee, not a hash accident.
 */
const TagChip: React.FC<Props> = ({ value, size = 'small', emptyPlaceholder = '—', sx }) => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return (
      <Typography variant="body2" color="text.disabled" component="span">
        {emptyPlaceholder}
      </Typography>
    );
  }

  const color = tagColor(trimmed);
  return (
    <Chip
      size={size}
      label={trimmed}
      sx={{
        bgcolor: alpha(color, 0.08),
        color: color,
        fontWeight: 500,
        fontSize: size === 'small' ? '0.7rem' : '0.75rem',
        height: size === 'small' ? 22 : 26,
        border: `1px solid ${alpha(color, 0.2)}`,
        '& .MuiChip-label': { px: 1 },
        ...(sx as any),
      }}
    />
  );
};

export default TagChip;
